import { Trash2, Edit, CalendarPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { categoryTypes } from '../../types';
import { formatCurrency } from '../../lib/format';
import type { Expense, Tag } from '../../types';
import type { ComponentProps } from 'react';

export interface ExpenseItemProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  expense: Expense;
  onEdit?: (expense: Expense) => void;
  onDelete?: (expense: Expense) => void;
  onRepeat?: (expense: Expense) => void;
  availableTags?: Tag[];
}

export function ExpenseItem({
  expense,
  onEdit,
  onDelete,
  onRepeat,
  availableTags = [],
  ...props
}: ExpenseItemProps) {
  const dateObj = new Date(expense.date);
  const formattedDate = dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
  const dayOnly = dateObj.toLocaleDateString('pt-BR', { day: '2-digit' });

  const categoryInfo = categoryTypes[expense.categoryType];

  const expenseTags = availableTags
    .filter((t) => expense.tags?.includes(t.id))
    .slice(0, 3);

  return (
    <div
      data-slot="expense-item"
      className="flex items-center gap-4 rounded-lg border border-border bg-surface p-1.5 transition-colors hover:bg-muted"
      {...props}
    >
      <div className="flex flex-col items-center justify-center rounded-lg bg-muted px-1.5 py-1.5 sm:px-3 sm:py-2">
        <span className="hidden text-xs text-muted-foreground sm:block">
          {formattedDate}
        </span>
        <span className="text-xs text-muted-foreground sm:hidden">
          {dayOnly}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex min-w-0 items-start justify-between">
          <div className="flex flex-1 min-w-0 flex-col gap-1">
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="size-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: categoryInfo.color }}
                title={categoryInfo.label}
              />
              <h4 className="text-sm text-foreground truncate">
                {expense.title}
              </h4>
            </div>
            {expense.description && (
              <p
                className="text-xs text-foreground-subtle truncate"
                title={expense.description}
              >
                {expense.description}
              </p>
            )}
            {expenseTags.length > 0 && (
              <div className="flex items-center gap-1">
                {expenseTags.map((tag) => (
                  <span
                    key={tag.id}
                    title={tag.name}
                    className="cursor-default text-sm leading-none"
                  >
                    {tag.icon}
                  </span>
                ))}
              </div>
            )}
          </div>
          <span className="text-sm font-bold text-foreground whitespace-nowrap">
            R$ {formatCurrency(expense.value)}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        {onRepeat && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRepeat(expense)}
            aria-label="Repetir no próximo mês"
            title="Repetir no próximo mês"
          >
            <CalendarPlus className="size-3.5" />
          </Button>
        )}
        {onEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(expense)}
            aria-label="Editar"
            title="Editar"
          >
            <Edit className="size-3.5" />
          </Button>
        )}
        {onDelete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(expense)}
            aria-label="Excluir"
            title="Excluir"
          >
            <Trash2 className="size-3.5 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  );
}
